import { memo, useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { ISubtitles } from 'src/interfaces/PlayerTemplate';
import { CheckIcon, ChevronRightIcon } from 'src/themes/cosmos/icons';
import useApplicationData from 'src/hooks/useApplicationData';
import { detectIOS } from 'src/utils/browserUtils';
import useKeyboardInteractions from 'src/hooks/useKeyboardInteractions';
import SettingOption from '../SettingOption';
import styles from './styles.module.scss';

export interface ISubtileControlProps {
  subtitles: ISubtitles[];
  activeSubtitle: ISubtitles | null;
  onChange: (subtitle: ISubtitles) => void;
  onDisable: () => void;
  onClickSubtitleStyle: () => void;
}

const SubtitleControl = ({
  subtitles,
  activeSubtitle,
  onChange,
  onDisable,
  onClickSubtitleStyle,
}: ISubtileControlProps) => {
  const { t } = useTranslation('common');
  const { flags } = useApplicationData();
  const { registerItem } = useKeyboardInteractions();

  const subtitleOptions = useMemo(
    () => [
      {
        kind: 'option',
        label: 'toggle.disable',
        language: '',
      },
      ...subtitles,
    ],
    [subtitles],
  );

  return (
    <>
      {flags.ENABLE_PLAYER_COSMOS_CUSTOM_SUBTITLE_STYLES && !detectIOS() && (
        <SettingOption
          className={styles.subtitleStyleButton}
          aria-label={t('settings.subtitle.style')}
          rightIcon={<ChevronRightIcon />}
          onClick={onClickSubtitleStyle}
        >
          {t('settings.subtitle.style')}
        </SettingOption>
      )}
      {subtitleOptions?.map((subtitle, index) => {
        const isActive = subtitle.language === activeSubtitle?.language;

        return subtitle.kind === 'option' ? (
          <SettingOption
            {...registerItem({ index })}
            key={subtitle.label}
            aria-label={t(subtitle.label)}
            active={!activeSubtitle}
            rightIcon={!activeSubtitle && <CheckIcon />}
            onClick={onDisable}
          >
            {t(subtitle.label)}
          </SettingOption>
        ) : (
          <SettingOption
            {...registerItem({ index, startWithFocus: isActive })}
            key={subtitle.label}
            aria-label={subtitle.label}
            active={isActive}
            rightIcon={isActive && <CheckIcon />}
            onClick={() => onChange(subtitle)}
          >
            {subtitle.label}
          </SettingOption>
        );
      })}
    </>
  );
};

export default memo(SubtitleControl);
